/**
 * CarManager — the Account Settings "Cars" tab.
 *
 * Lists the user's cars (name, usage, the alternate names folded into each), and
 * lets them rename, edit physics params (via the shared VehicleProfileEditor), and
 * merge two names that are really the same car. Different cars are simply never
 * merged.
 *
 * autoOpenPhysics/initialCarId open the Physics editor straight on a specific
 * car (e.g. from a session's "Car Physics" shortcut). When that car matches
 * liveSessionCarId — the currently loaded session's car — the editor gets the
 * session's live value/onChange/sessionId/racer/onMetaSaved instead of the
 * plain per-car "Save to car" flow, reproducing the old direct-from-session
 * live-tuning behavior.
 */
import { useState, useEffect, useCallback } from 'react';
import { getCars, patchCar, mergeCars, deleteCar } from '../../services/api';
import VehicleProfileEditor from '../Analysis/VehicleProfileEditor';
import styles from './CarManager.module.css';

export default function CarManager({
  initialCarId, autoOpenPhysics,
  liveSessionCarId, liveValue, onLiveChange,
  sessionId, racer, onMetaSaved,
}) {
  const [cars, setCars] = useState(null);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);   // inline name edit
  const [nameDraft, setNameDraft] = useState('');
  const [physicsCar, setPhysicsCar] = useState(null); // car being edited in the modal

  const load = useCallback(() => {
    getCars().then(setCars).catch(e => setError(String(e.message || e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-open the Physics editor for a specific car — used when arriving here
  // from a session's "Car Physics" shortcut instead of browsing the list.
  useEffect(() => {
    if (!autoOpenPhysics || !initialCarId || !cars) return;
    const car = cars.find(c => c.id === initialCarId);
    if (car) setPhysicsCar(car);
  }, [autoOpenPhysics, initialCarId, cars]);

  const saveName = useCallback(async (car) => {
    const name = nameDraft.trim();
    setEditingId(null);
    if (!name || name === car.name) return;
    try { await patchCar(car.id, { name }); load(); }
    catch (e) { setError(String(e.message || e)); }
  }, [nameDraft, load]);

  const doMerge = useCallback(async (sourceCar, targetId) => {
    const target = cars.find(c => c.id === targetId);
    if (!target) return;
    if (!window.confirm(
      `Merge "${sourceCar.name}" into "${target.name}"?\n\n`
      + `All of "${sourceCar.name}"'s sessions and laps move to "${target.name}", `
      + `and future uploads of that name map there too. This can't be auto-undone.`)) return;
    try {
      await mergeCars(targetId, sourceCar.id);
      load();
    } catch (e) { setError(String(e.message || e)); }
  }, [cars, load]);

  const removeCar = useCallback(async (car) => {
    if (!window.confirm(`Delete the car "${car.name}"? Its lap data is kept; only the saved name/params record is removed.`)) return;
    try { await deleteCar(car.id); load(); }
    catch (e) { setError(String(e.message || e)); }
  }, [load]);

  if (error) return <div className={styles.error}>Couldn’t load cars: {error}</div>;
  if (cars === null) return <div className={styles.muted}>Loading…</div>;
  if (cars.length === 0) return <div className={styles.muted}>No cars yet — upload a session and your car appears here.</div>;

  return (
    <div className={styles.wrap}>
      <p className={styles.intro}>
        Your cars are detected from the <em>Vehicle</em> name in your uploads. If one
        car got logged under two names, merge them — different cars stay separate.
      </p>

      {cars.map(car => (
        <div key={car.id} className={styles.card}>
          <div className={styles.cardMain}>
            {editingId === car.id ? (
              <input
                className={styles.nameInput} autoFocus value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                onBlur={() => saveName(car)}
                onKeyDown={e => { if (e.key === 'Enter') saveName(car); if (e.key === 'Escape') setEditingId(null); }}
              />
            ) : (
              <button className={styles.name} title="Rename"
                      onClick={() => { setEditingId(car.id); setNameDraft(car.name); }}>
                {car.name} <span className={styles.pencil}>✎</span>
              </button>
            )}
            <span className={styles.usage}>{car.n_sessions} sessions · {car.n_laps} laps</span>
          </div>

          {car.aliases.length > 0 && (
            <div className={styles.aliases}>also logged as: {car.aliases.join(', ')}</div>
          )}

          <div className={styles.actions}>
            <button className={styles.btn} onClick={() => setPhysicsCar(car)}>Physics</button>
            {cars.length > 1 && (
              <select className={styles.mergeSel} value=""
                      onChange={e => { if (e.target.value) doMerge(car, e.target.value); e.target.value = ''; }}>
                <option value="">Merge into…</option>
                {cars.filter(c => c.id !== car.id).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <button className={styles.btnGhost} onClick={() => removeCar(car)}>Delete</button>
          </div>
        </div>
      ))}

      {physicsCar && physicsCar.id === liveSessionCarId ? (
        <VehicleProfileEditor
          value={liveValue}
          onChange={onLiveChange}
          onClose={() => { setPhysicsCar(null); load(); }}
          sessionId={sessionId}
          racer={racer}
          onMetaSaved={onMetaSaved}
        />
      ) : physicsCar && (
        <VehicleProfileEditor
          value={{
            car_id: physicsCar.id,
            vehicle_name: physicsCar.name,
            mass_kg: physicsCar.mass_kg ?? 1300,
            cd: physicsCar.cd ?? 0.32,
            frontal_area_m2: physicsCar.frontal_area_m2 ?? 2.1,
            crr: physicsCar.crr ?? 0.013,
            smoothing_m: physicsCar.smoothing_m ?? 80,
            saved: physicsCar.mass_kg != null,
            owned: true,
          }}
          onChange={() => {}}
          onClose={() => { setPhysicsCar(null); load(); }}
        />
      )}
    </div>
  );
}
